import { SxProps, Theme } from '@mui/material/styles';

export const opportunityPipelineSx: SxProps<Theme> = theme => ({
  width: '100%',
  '& .step-description__wrapper': {
    mt: '20px',
    [theme.breakpoints.up('smallMobile')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      columnGap: '20px',
    },
    '& h4': {
      m: 0,
      mb: '1.25em',
      fontSize: 'inherit',
      color: 'inherit',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    '& ul': {
      mt: 0,
      pl: '25px',
    },
    '& .field-label': {
      color: 'text.secondary',
      mr: '5px',
    },
    '& .step-description__attributes': {
      pb: '1em',
      mt: '1.5em',
      [theme.breakpoints.up('smallMobile')]: {
        mt: 0,
      },
    },
    '& .step-description__item': {
      width: '100%',
      mb: '1em',
      ml: 'auto',
      [theme.breakpoints.up('smallMobile')]: {
        maxWidth: '420px',
      },
      '&:last-of-type': {
        mb: 'auto',
      },
    },
    '& .step-button': {
      ml: 'auto',
      mt: '20px',
    },
  },
});

const getStepStatusSx = (backgroundColor: string, hoverBackgroundColor: string) => ({
  background: backgroundColor,
  color: 'common.white',
  '&::before': {
    boxShadow: `inset 0 8px 0 8px ${backgroundColor}, inset -6px 8px 0 8px ${backgroundColor}`,
  },
  '&::after': {
    boxShadow: `inset 0 -8px 0 8px ${backgroundColor}, inset -6px -8px 0 8px ${backgroundColor}`,
  },
  '&:first-of-type': {
    boxShadow: `15px 0 0 0 ${backgroundColor}`,
  },
  '&:hover': {
    background: hoverBackgroundColor,
    '&::before': {
      boxShadow: `inset 0 16px 0 16px ${hoverBackgroundColor}, inset -6px 8px 0 8px ${hoverBackgroundColor}`,
    },
    '&::after': {
      boxShadow: `inset 0 -16px 0 16px ${hoverBackgroundColor}, inset -6px -8px 0 8px ${hoverBackgroundColor}`,
    },
  },
});

export const opportunityStepperSx: SxProps<Theme> = theme => {
  const stepBorderColor = theme.palette.border?.default ?? theme.palette.divider;
  const activeStepBorderColor = theme.palette.border?.strong ?? theme.palette.text.primary;
  const inactiveStepBackground = theme.palette.background.default;
  const controlBackground = theme.palette.background.paper;
  const hoverStepBackground = stepBorderColor;

  return {
    '& .opportunity-steps__control': {
      border: `1px solid ${stepBorderColor}`,
      py: '5px',
      px: 0,
      borderRadius: 0,
      zIndex: 1,
      transition: 'none',
      '&:last-child': {
        transform: 'rotate(180deg)',
      },
      '&::before, &::after': {
        content: '""',
        left: '-1px',
        position: 'absolute',
        height: 'calc(50% + 1px)',
        width: '100%',
        borderLeft: `1px solid ${stepBorderColor}`,
      },
      '&::before': {
        borderTop: `1px solid ${stepBorderColor}`,
        transformOrigin: '0 0',
        transform: 'skewX(-30deg)',
        top: '-1px',
        boxShadow: `inset 0 10px 0 10px ${controlBackground}, inset -6px 10px 0 10px ${controlBackground}`,
      },
      '&::after': {
        borderBottom: `1px solid ${stepBorderColor}`,
        transformOrigin: '0 100%',
        transform: 'skewX(30deg)',
        bottom: '-1px',
        boxShadow: `inset 0 -10px 0 10px ${controlBackground}, inset -6px -10px 0 10px ${controlBackground}`,
      },
      '& svg': {
        zIndex: 2,
        transform: 'translateX(-3px) scale(1.2)',
        width: '20px',
      },
      '&:hover': {
        background: hoverStepBackground,
        '&::before': {
          boxShadow: `inset 0 10px 0 10px ${hoverStepBackground}, inset -6px 10px 0 10px ${hoverStepBackground}`,
        },
        '&::after': {
          boxShadow: `inset 0 -10px 0 10px ${hoverStepBackground}, inset -6px -10px 0 10px ${hoverStepBackground}`,
        },
      },
    },
    '& .opportunity-steps': {
      flexShrink: 1,
      p: 0.25,
      overflow: 'auto',
      contain: 'inline-size',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    '& .stepper_step-label': {
      zIndex: 2,
      position: 'relative',
      py: '10px',
      px: '15px',
      m: 0,
      transform: 'translateX(-3px)',
      width: '100%',
      whiteSpace: 'nowrap',
      '& svg': {
        position: 'absolute',
        top: '50%',
        right: 0,
        transform: 'translateY(calc(-50% - 1px))',
      },
    },
    '& .stepper_step': {
      p: 0,
      background: inactiveStepBackground,
      position: 'relative',
      ml: '22px',
      lineHeight: '16px',
      textAlign: 'center',
      zIndex: 1,
      minWidth: '135px',
      transition: 'none',
      flexShrink: 1,
      color: 'text.primary',
      borderRadius: 0,
      '&::before, &::after': {
        content: '""',
        left: '-15px',
        position: 'absolute',
        height: '50%',
        width: 'calc(100% + 16px)',
        borderLeft: `1px solid ${stepBorderColor}`,
        borderRight: `1px solid ${stepBorderColor}`,
      },
      '&::before': {
        borderTop: `1px solid ${stepBorderColor}`,
        transformOrigin: '0 0',
        transform: 'skewX(30deg)',
        top: 0,
        boxShadow: `inset 0 8px 0 8px ${inactiveStepBackground}, inset -6px 8px 0 8px ${inactiveStepBackground}`,
      },
      '&::after': {
        borderBottom: `1px solid ${stepBorderColor}`,
        transformOrigin: '0 100%',
        transform: 'skewX(-30deg)',
        bottom: 0,
        boxShadow: `inset 0 -8px 0 8px ${inactiveStepBackground}, inset -6px -8px 0 8px ${inactiveStepBackground}`,
      },
      '&:first-of-type': {
        left: '-15px',
        boxShadow: `15px 0 0 0 ${inactiveStepBackground}`,
        borderLeft: `1px solid ${stepBorderColor}`,
        mr: '-1px',
        ml: '15px',
        '& .stepper_step-label': {
          transform: 'translateX(7px)',
        },
      },
      '&:first-of-type::before, &:first-of-type::after': {
        left: '-1px',
        borderLeft: '0 !important',
      },
      '&:first-of-type:hover': {
        boxShadow: `15px 0 0 0 ${hoverStepBackground}`,
      },
      '&:last-of-type': {
        left: 0,
        boxShadow: `inset -1px 0 0 0 ${stepBorderColor}, inset 0 -1px 0 0 ${stepBorderColor}, inset 0 1px 0 0 ${stepBorderColor}`,
        border: 0,
        pl: '15px',
        '& .stepper_step-label': {
          transform: 'translateX(-13px)',
          '& svg': {
            right: '-7px',
          },
        },
      },
      '&:last-of-type::before, &:last-of-type::after': {
        left: '-15px',
        borderRight: '0 !important',
        width: '100%',
      },
      '&:last-of-type:hover': {
        background: hoverStepBackground,
      },
      '&:hover': {
        background: hoverStepBackground,
        '&::before': {
          boxShadow: `inset 0 16px 0 16px ${hoverStepBackground}, inset -6px 8px 0 8px ${hoverStepBackground}`,
        },
        '&::after': {
          boxShadow: `inset 0 -16px 0 16px ${hoverStepBackground}, inset -6px -8px 0 8px ${hoverStepBackground}`,
        },
      },
      '&.stepper_step--completed': getStepStatusSx(theme.palette.success.main, theme.palette.success.dark),
      '&.stepper_step--failed': getStepStatusSx(theme.palette.error.main, theme.palette.error.dark),
      '&.stepper_step--postponed': getStepStatusSx(theme.palette.warning.main, theme.palette.warning.dark),
      '&.stepper_step--current': getStepStatusSx(theme.palette.primary.main, theme.palette.primary.dark),
      '&.stepper_step--active': {
        pointerEvents: 'none',
        '&::before, &::after': {
          borderLeft: `2px solid ${activeStepBorderColor}`,
          borderRight: `2px solid ${activeStepBorderColor}`,
        },
        '&::before': {
          borderTop: `2px solid ${activeStepBorderColor}`,
        },
        '&::after': {
          borderBottom: `2px solid ${activeStepBorderColor}`,
        },
        '&:first-of-type': {
          borderLeft: `2px solid ${activeStepBorderColor}`,
        },
        '&:last-of-type': {
          boxShadow: `inset -2px 0 0 0 ${activeStepBorderColor}, inset 0 -2px 0 0 ${activeStepBorderColor}, inset 0 2px 0 0 ${activeStepBorderColor}`,
        },
      },
    },
  };
};
